import React from 'react'
import { useTranslation } from 'react-i18next'
import { FileText, Calendar, Gavel } from 'lucide-react'
import { InfoBox } from '../../ui'
import { 
 StepCard, 
 FormField,
 FormGrid,
 PlateInput, 
 SelectField, 
 SelectOption,
 VehicleDocumentUpload,
 VehicleDocument,
 TextareaField,
 ConditionalCheckboxField,
 DatePicker
} from '../../form'
import { VehicleExistingDocuments, ExistingVehicleDocument } from '../'
import { Input } from '../../ui'
import { formatChassis, formatRenavam } from '../../../utils/formatters'

interface VehicleEditStep4DocumentationProps {
 formData: {
 category: string
 plate?: string
 chassis?: string
 renavam?: string
 documents: VehicleDocument[]
 isAuction: boolean
 auctionCategory?: string
 auctionSubtype?: string
 auctionDamageLevel?: string
 isInspected: boolean
 inspectionDate?: string
 armorLevel?: string
 specialCategory?: string
 characteristics?: string[]
 description: string
 }
 existingDocuments?: ExistingVehicleDocument[]
 onRemoveExistingDocument?: (docId: string) => void
 errors: Record<string, string>
 vehicleOptions: any
 onInputChange: (field: string, value: any) => void
 }

export const VehicleEditStep4Documentation: React.FC<VehicleEditStep4DocumentationProps> = ({
 formData,
 existingDocuments = [],
 onRemoveExistingDocument,
 errors,
 vehicleOptions,
 onInputChange,
}) => {
 const { t } = useTranslation()

 const handleInspectedChange = (checked: boolean) => {
 if (!checked) {
  onInputChange('isInspected', false)
  onInputChange('inspectionDate', '')
 } else {
  onInputChange('isInspected', true)
 }
}

 const handleAuctionChange = (checked: boolean) => {
 if (!checked) {
  onInputChange('isAuction', false)
  onInputChange('auctionCategory', '')
  onInputChange('auctionSubtype', '')
  onInputChange('auctionDamageLevel', '')
 } else {
  onInputChange('isAuction', true)
 }
}

 // Converte opções para SelectOption — dados ficam em vehicleOptions.auctions.*
 const auctionCategoryOptions: SelectOption[] = vehicleOptions?.auctions?.categories?.map((cat: any) => ({
 value: cat.value,
 label: cat.label
 })) || []

 // Subtipo dinâmico baseado na categoria selecionada
 const getSubtypeOptions = (category: string): SelectOption[] => {
 if (!category || !vehicleOptions?.auctions) return []
 const map: Record<string, any[]> = {
 insurance: vehicleOptions.auctions.insurance || [],
 bank: vehicleOptions.auctions.bank || [],
 judicial: vehicleOptions.auctions.judicial || [],
 federal: vehicleOptions.auctions.federal || [],
 police: vehicleOptions.auctions.police || [],
 fleet: vehicleOptions.auctions.fleet || [],
 private: vehicleOptions.auctions.private || [],
 }
 return (map[category] || []).map((s: any) => ({ value: s.value, label: s.label }))
 }

 const auctionSubtypeOptions: SelectOption[] = getSubtypeOptions(formData.auctionCategory || '')

 const damageLevelOptions: SelectOption[] = vehicleOptions?.auctions?.damageLevels?.map((level: any) => ({
 value: level.value,
 label: level.label
 })) || []

 return (
 <>
 {/* Documentação */}
 <StepCard title={t('vehicleRegistration.documentation.title')} icon={FileText}>
 <FormGrid columns={3}>
 <FormField label={t('vehicleRegistration.documentation.plateLabel')} error={errors.plate}>
 <PlateInput
 value={formData.plate || ''}
 onChange={(value) => onInputChange('plate', value)}
 error={errors.plate}
 />
 </FormField>

 <FormField label={t('vehicleRegistration.documentation.chassisLabel')} error={errors.chassis}>
 <Input
 type="text"
 value={formData.chassis || ''}
 onChange={(e) => onInputChange('chassis', formatChassis(e.target.value))}
 placeholder={t('vehicleRegistration.documentation.chassisPlaceholder')}
 maxLength={17}
 className="uppercase"
 />
 </FormField>

 <FormField label={t('vehicleRegistration.documentation.renavamLabel')} error={errors.renavam}>
 <Input
 type="text"
 value={formData.renavam || ''}
 onChange={(e) => onInputChange('renavam', formatRenavam(e.target.value))}
 placeholder={t('vehicleRegistration.documentation.renavamPlaceholder')}
 maxLength={11}
 />
 </FormField>
 </FormGrid>

 {/* Documentos Existentes */}
 {existingDocuments && existingDocuments.length > 0 && (
 <div className="mb-4">
 <VehicleExistingDocuments 
 documents={existingDocuments}
 onRemoveDocument={onRemoveExistingDocument}
 readOnly={!onRemoveExistingDocument}
 />
 </div>
 )}

 {/* Upload de Novos Documentos */}
 <VehicleDocumentUpload
 label={existingDocuments && existingDocuments.length > 0 ? t('vehicleRegistration.documentation.addDocumentsLabel') : t('vehicleRegistration.documentation.documentsLabel')}
 documents={formData.documents}
 onDocumentsChange={(docs) => onInputChange('documents', docs)}
 accept=".pdf,.jpg,.jpeg,.png"
 hint={t('vehicleRegistration.documentation.documentsHint')}
 />
 </StepCard>

 {/* Vistoria */}
 <StepCard title={t('vehicleRegistration.documentation.inspectionTitle')} icon={Calendar}>
 <ConditionalCheckboxField
 label={t('vehicleRegistration.documentation.inspectedLabel')}
 description={t('vehicleRegistration.documentation.inspectedDescription')}
 checked={formData.isInspected}
 onChange={handleInspectedChange}
 activeBadge={t('vehicleRegistration.documentation.inspectedBadge')}
 >
 <DatePicker
 label={t('vehicleRegistration.documentation.inspectionDateLabel')}
 value={formData.inspectionDate || ''}
 onChange={(value) => onInputChange('inspectionDate', value)}
 error={errors.inspectionDate}
 placeholder={t('vehicleRegistration.documentation.inspectionDatePlaceholder')}
 />
 </ConditionalCheckboxField>
 </StepCard>

 {/* Leilão */}
 <StepCard title={t('vehicleRegistration.documentation.auctionTitle')} icon={Gavel}>
 <ConditionalCheckboxField
 label={t('vehicleRegistration.documentation.auctionLabel')}
 description={t('vehicleRegistration.documentation.auctionDescription')}
 checked={formData.isAuction}
 onChange={handleAuctionChange}
 activeBadge={t('vehicleRegistration.documentation.auctionBadge')}
 >
 <FormGrid columns={2}>
 <SelectField
 label={t('vehicleRegistration.documentation.auctionCategoryLabel')}
 value={formData.auctionCategory || ''}
 options={auctionCategoryOptions}
 onChange={(value) => onInputChange('auctionCategory', value)}
 placeholder={t('vehicleRegistration.documentation.auctionCategoryPlaceholder')}
 />

 <SelectField
 label={t('vehicleRegistration.documentation.auctionSubtypeLabel')}
 value={formData.auctionSubtype || ''}
 options={auctionSubtypeOptions}
 onChange={(value) => onInputChange('auctionSubtype', value)}
 placeholder={t('vehicleRegistration.documentation.auctionSubtypePlaceholder')}
 />
 </FormGrid>

 <SelectField
 label={t('vehicleRegistration.documentation.damageLevelLabel')}
 value={formData.auctionDamageLevel || ''}
 options={damageLevelOptions}
 onChange={(value) => onInputChange('auctionDamageLevel', value)}
 placeholder={t('vehicleRegistration.documentation.damageLevelPlaceholder')}
 />
 </ConditionalCheckboxField>

 <InfoBox variant="warning">
 <strong>{t('vehicleRegistration.documentation.importanceTitle')}</strong> {t('vehicleRegistration.documentation.importanceMessage')}
 </InfoBox>
 </StepCard>

 {/* Descrição */}
 <StepCard
   title={t('vehicleRegistration.documentation.descriptionTitle')}
   icon={FileText}
   description={t('vehicleRegistration.documentation.descriptionSubtitle')}
 >
 <TextareaField
 label={t('vehicleRegistration.documentation.descriptionLabel')}
 value={formData.description}
 onChange={(value) => onInputChange('description', value)}
 placeholder={t('vehicleRegistration.documentation.descriptionPlaceholder')}
 required
 error={errors.description}
 rows={6}
 maxLength={1000}
 showCharCount
 hint={t('vehicleRegistration.documentation.descriptionHintEdit')}
 />

 <InfoBox variant="info">
 <strong>{t('vehicleRegistration.documentation.tipTitle')}</strong> {t('vehicleRegistration.documentation.tipMessage')}
 <ul className="list-disc list-inside mt-1 space-y-0.5">
 <li>{t('vehicleRegistration.documentation.tip1')}</li>
 <li>{t('vehicleRegistration.documentation.tip2')}</li>
 <li>{t('vehicleRegistration.documentation.tip3')}</li>
 <li>{t('vehicleRegistration.documentation.tip4')}</li>
 </ul>
 </InfoBox>
 </StepCard>
 </>
 )
}
